import { useEffect, useState, useMemo } from 'react'
import { reviewService, Review } from '@/services/reviewService'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Star } from 'lucide-react'
import DataTable from '@/components/ui/DataTable'
import FilterBar, { booleanFilter } from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'

const ReviewsPage = () => {
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })

  const loadReviews = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await reviewService.getAll()
      if (response.success) {
        setReviews(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }))
      } else {
        setError('Échec du chargement des avis')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReviews()
  }, [])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const toggleApproval = async (id: string, currentApproval: boolean) => {
    try {
      await reviewService.updateApproval(id, !currentApproval)
      setReviews(prev => prev.map(review => 
        review.review_id === id ? { ...review, is_approved: !currentApproval } : review
      ))
    } catch (err) {
      console.error('Failed to update review approval:', err)
    }
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    )
  }

  const columns = useMemo(() => [
    {
      key: 'user',
      label: 'Utilisateur',
      sortable: true,
      render: (row: Review) => (
        <div className="font-medium">
          {row.user_first_name && row.user_last_name
            ? `${row.user_first_name} ${row.user_last_name}`
            : `Utilisateur ${row.user_id.substring(0, 8)}...`}
        </div>
      )
    },
    {
      key: 'merchant_name',
      label: 'Commerçant',
      sortable: true,
      render: (value: string | null, row: Review) => (
        <div>{value || `Commerçant ${row.merchant_id.substring(0, 8)}...`}</div>
      )
    },
    {
      key: 'rating',
      label: 'Note',
      sortable: true,
      render: (value: number) => (
        <div className="flex items-center gap-2">
          {renderStars(value)}
          <span className="font-bold">{value}/5</span>
        </div>
      )
    },
    {
      key: 'comment',
      label: 'Commentaire',
      sortable: false,
      render: (value: string) => (
        <div className="max-w-xs truncate">{value || 'Aucun commentaire'}</div>
      )
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => formatDate(value)
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (row: Review) => (
        <div className="flex flex-col gap-1">
          <Badge variant={row.is_approved ? 'default' : 'secondary'}>
            {row.is_approved ? 'Approuvé' : 'En attente'}
          </Badge>
          {row.is_verified_purchase && (
            <Badge variant="outline" className="text-xs">
              Achat vérifié
            </Badge>
          )}
        </div>
      )
    }
  ], [])

  const actions = useMemo(() => [
    {
      key: 'approve',
      label: 'Approuver',
      icon: 'Check',
      variant: 'default' as const,
      size: 'sm' as const,
      show: (row: Review) => !row.is_approved,
      onClick: (row: Review) => toggleApproval(row.review_id, row.is_approved)
    },
    {
      key: 'disapprove',
      label: 'Désapprouver',
      icon: 'X',
      variant: 'outline' as const,
      size: 'sm' as const,
      show: (row: Review) => row.is_approved,
      onClick: (row: Review) => toggleApproval(row.review_id, row.is_approved)
    }
  ], [])

  const filterOptions = useMemo(() => [
    {
      key: 'rating',
      label: 'Note',
      type: 'select' as const,
      options: [
        { value: '5', label: '5 étoiles' },
        { value: '4', label: '4 étoiles' },
        { value: '3', label: '3 étoiles' },
        { value: '2', label: '2 étoiles' },
        { value: '1', label: '1 étoile' }
      ]
    },
    booleanFilter('is_approved', 'Statut d\'approbation'),
    booleanFilter('is_verified_purchase', 'Achat vérifié'),
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Rechercher par utilisateur ou commerçant...'
    }
  ], [])

  const filteredData = useMemo(() => {
    let result = [...reviews]

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(review => 
        (review.user_first_name && review.user_first_name.toLowerCase().includes(query)) ||
        (review.user_last_name && review.user_last_name.toLowerCase().includes(query)) ||
        (review.merchant_name && review.merchant_name.toLowerCase().includes(query)) ||
        review.comment.toLowerCase().includes(query)
      )
    }

    if (activeFilters.length > 0) {
      activeFilters.forEach(filter => {
        if (filter.key === 'rating' && filter.value) {
          result = result.filter(review => review.rating === parseInt(filter.value))
        }
        if (filter.key === 'is_approved' && filter.value !== undefined) {
          result = result.filter(review => review.is_approved === filter.value)
        }
        if (filter.key === 'is_verified_purchase' && filter.value !== undefined) {
          result = result.filter(review => review.is_verified_purchase === filter.value)
        }
      })
    }

    return result
  }, [reviews, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const stats = useMemo(() => {
    const averageRating = reviews.length > 0 
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0
    const approvedCount = reviews.filter(r => r.is_approved).length
    const verifiedCount = reviews.filter(r => r.is_verified_purchase).length

    return {
      total: reviews.length,
      averageRating,
      approvedCount,
      verifiedCount,
      byRating: reviews.reduce((acc, review) => {
        acc[review.rating] = (acc[review.rating] || 0) + 1
        return acc
      }, {} as Record<number, number>),
      byMerchant: reviews.reduce((acc, review) => {
        const merchantName = review.merchant_name || `Commerçant ${review.merchant_id.substring(0, 8)}...`
        if (!acc[merchantName]) {
          acc[merchantName] = {
            count: 0,
            totalRating: 0,
            approvedCount: 0
          }
        }
        acc[merchantName].count++
        acc[merchantName].totalRating += review.rating
        if (review.is_approved) acc[merchantName].approvedCount++
        return acc
      }, {} as Record<string, { count: number; totalRating: number; approvedCount: number }>)
    }
  }, [reviews])

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleFilterChange = (filters: any[]) => {
    setActiveFilters(filters)
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleRefresh = () => {
    loadReviews()
  }

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Avis"
        subtitle="Gérez les avis des utilisateurs sur les commerçants"
        breadcrumbs={[
          { label: 'Dashboard', href: '/' },
          { label: 'Avis', href: '/reviews' }
        ]}
        actions={[
          {
            label: loading ? 'Chargement...' : 'Actualiser',
            onClick: handleRefresh,
            disabled: loading
          }
        ]}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Total avis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Note moyenne</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.averageRating.toFixed(1)}
              <span className="text-lg text-muted-foreground ml-1">/5</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Avis approuvés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.approvedCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Achats vérifiés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.verifiedCount}</div>
          </CardContent>
        </Card>
      </div>

      <FilterBar
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        searchPlaceholder="Rechercher par utilisateur ou commerçant..."
      />

      <DataTable
        data={paginatedData}
        columns={columns}
        actions={actions}
        loading={loading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: filteredData.length,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
        emptyMessage="Aucun avis trouvé"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Répartition par note</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = stats.byRating[rating] || 0
                const percentage = reviews.length > 0 
                  ? Math.round((count / reviews.length) * 100) 
                  : 0
                return (
                  <div key={rating}>
                    <div className="flex justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {renderStars(rating)}
                        <span className="font-medium">{rating} étoiles</span>
                      </div>
                      <span className="text-sm font-medium">{count} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Statut des avis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <div>
                    <span className="font-medium">Approuvés</span>
                    <span className="ml-2 text-sm text-muted-foreground">
                      ({reviews.filter(r => r.is_approved && r.is_verified_purchase).length} vérifiés)
                    </span>
                  </div>
                  <span className="text-sm font-medium">
                    {stats.approvedCount} 
                    ({Math.round((stats.approvedCount / reviews.length) * 100 || 0)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full"
                    style={{ 
                      width: `${Math.round((stats.approvedCount / reviews.length) * 100 || 0)}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <div>
                    <span className="font-medium">En attente</span>
                    <span className="ml-2 text-sm text-muted-foreground">
                      ({reviews.filter(r => !r.is_approved && r.is_verified_purchase).length} vérifiés)
                    </span>
                  </div>
                  <span className="text-sm font-medium">
                    {reviews.filter(r => !r.is_approved).length} 
                    ({Math.round((reviews.filter(r => !r.is_approved).length / reviews.length) * 100 || 0)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-500 rounded-full"
                    style={{ 
                      width: `${Math.round((reviews.filter(r => !r.is_approved).length / reviews.length) * 100 || 0)}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Top 5 des commerçants</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.byMerchant)
              .map(([merchantName, data]) => ({
                merchantName,
                averageRating: data.count > 0 ? data.totalRating / data.count : 0,
                reviewCount: data.count,
                approvedCount: data.approvedCount
              }))
              .sort((a, b) => b.averageRating - a.averageRating)
              .slice(0, 5)
              .map((merchant, index) => (
                <div key={merchant.merchantName} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex items-center justify-center bg-primary-100 text-primary-700 rounded-md mr-4">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium">{merchant.merchantName}</div>
                      <div className="text-sm text-muted-foreground">
                        {merchant.reviewCount} avis ({merchant.approvedCount} approuvés)
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {renderStars(Math.round(merchant.averageRating))}
                      <span className="text-2xl font-bold">{merchant.averageRating.toFixed(1)}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">Note moyenne</div>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ReviewsPage
